// PolyGen.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './polygen.css';
import './polygen-light.css';
import { ensurePolyGenApiKey } from './WebserverUser';
import {
  createServiceUpload,
  defaultServicePipeline,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
} from '../shared/xomics-webserver-client';
import CentralApiGuide from '../shared/CentralApiGuide';
import WebserverDemoSection from '../shared/WebserverDemoSection';
import WebserverReportPanel from '../shared/WebserverReportPanel';

import logoDark from '../../POLYGEN.png';
import logoLight from '../../POLYGEN_Dark.png';
import baseLogo from '../../base_logo.png';
import baseLogoLight from '../../base_logo_light.png';

import docScreenshot1 from '../../p1.jpeg';
import docScreenshot2 from '../../p2.png';
import docScreenshot3 from '../../p3.png';
import docScreenshot4 from '../../p4.png';

const THEME_STORAGE_KEY = 'pg-theme-preference';
const SERVICE_CODE = 'gpgs';

// Apply theme immediately on script load to prevent flash
(function applyStoredThemeImmediately() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  let theme = 'dark';
  if (stored === 'light') theme = 'light';
  else if (stored === 'system') {
    theme = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  const root = document.documentElement;
  root.classList.remove('pg-theme-light', 'pg-theme-dark');
  root.classList.add(theme === 'light' ? 'pg-theme-light' : 'pg-theme-dark');
})();

function PolyGen() {
  const [apiKey, setApiKey] = useState('');
  const [theme, setTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState('home');

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // New job form state
  const [sampleName, setSampleName] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [sampleCode, setSampleCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Polling ref
  const pollIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const ageFromDateOfBirth = (value) => {
    const dob = new Date(`${value}T00:00:00`);
    if (Number.isNaN(dob.getTime()) || dob > new Date()) return null;
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    if (today.getMonth() < dob.getMonth()
      || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) years -= 1;
    return years >= 0 && years <= 130 ? years : null;
  };

  // Initialize API key and fetch data
  useEffect(() => {
    const init = async () => {
      const key = await ensurePolyGenApiKey();
      setApiKey(key);
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored) setTheme(stored);
    };
    init();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Fetch jobs when API key is ready
  useEffect(() => {
    if (apiKey) {
      loadServiceCatalogue(SERVICE_CODE, apiKey)
        .then((catalogue) => {
          const selected = defaultServicePipeline(catalogue);
          setPipeline(selected);
          if (selected.sample_codes?.length === 1) setSampleCode(selected.sample_codes[0]);
        })
        .catch((error) => console.error('Failed to load G-PGS pipeline:', error));
      fetchJobs();
      startJobPolling();
    }
  }, [apiKey]);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      if (!apiKey) return;
      const data = await loadServiceJobs(SERVICE_CODE, apiKey, { limit: 50 });
      setJobs(data.jobs);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, [apiKey]);

  // Poll for job updates
  const startJobPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(() => {
      fetchJobs();
    }, 10000);
  };

  const handleThemeToggle = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    const root = document.documentElement;
    root.classList.remove('pg-theme-light', 'pg-theme-dark');
    root.classList.add(newTheme === 'light' ? 'pg-theme-light' : 'pg-theme-dark');
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.sample_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.job_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // File handling
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validExts = ['.vcf', '.vcf.gz', '.gz'];
      const isValid = validExts.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!isValid) {
        alert('Please select a .vcf.gz file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) setSelectedFile(file);
  };

  // Submit job: create -> upload -> confirm
  const handleSubmit = async () => {
    if (!sampleName || !gender || !age || !selectedFile || !pipeline || !sampleCode) {
      alert('Please fill in all required fields and select a VCF file.');
      return;
    }

    const patientAge = ageFromDateOfBirth(age);
    if (patientAge === null) {
      alert('Please enter a valid date of birth.');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      await createServiceUpload({
        serviceCode: SERVICE_CODE,
        token: apiKey,
        pipeline,
        sampleCode,
        selectedFiles: [{ file: selectedFile, slotPosition: 1, label: 'Called VCF' }],
        patientParams: { age: patientAge, sex: gender },
        displayName: sampleName,
        metadata: {
          gender,
          age: patientAge,
          date_of_birth: age,
        },
        onProgress: ({ fraction }) => setUploadProgress(Math.round(fraction * 100)),
      });

      // Success - refresh jobs and reset form
      await fetchJobs();
      setSampleName('');
      setGender('');
      setAge('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

    } catch (err) {
      console.error('Submit error:', err);
      alert(err.message);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Results modal state
  const [resultsJob, setResultsJob] = useState(null); // full job object or null

  const openResultsModal = (job) => {
    setResultsJob(job);
  };

  const closeResultsModal = () => {
    setResultsJob(null);
  };

  // Download all result files
  const handleDownloadResults = async () => {
    try {
      await openJobDownload(SERVICE_CODE, apiKey, resultsJob);
    } catch (err) {
      console.error('Download error:', err);
      alert(err.message);
    }
  };

  // Status helpers
  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return 'fa-spinner fa-spin';
      case 'completed': return 'fa-check';
      case 'failed': return 'fa-times';
      case 'queued': return 'fa-clock';
      case 'pending': return 'fa-hourglass-start';
      case 'uploading': return 'fa-upload';
      case 'cancelled': return 'fa-ban';
      default: return 'fa-circle';
    }
  };

  const logoSrc = theme === 'light' ? logoLight : logoDark;
  const baseLogoSrc = theme === 'light' ? baseLogoLight : baseLogo;
  const themeClass = theme === 'light' ? 'light-theme' : '';

  return (
    <div className={`PG-app-container ${themeClass}`}>
      {/* AppBar Header */}
      <header className="PG-header">
        <div className="PG-header-left">
          <a href="/" className="PG-logo-container">
            <img src={baseLogoSrc} alt="Home" className="PG-logo" />
          </a>
          <nav className="PG-nav-tabs">
            {['home', 'api', 'help', 'settings'].map(tab => (
              <button
                key={tab}
                className={`PG-nav-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="PG-content-area">
        {activeTab === 'settings' ? (
          <div className="PG-settings-container">
            <div className="PG-settings-content">
              <h1>Settings</h1>
              <div className="PG-settings-section">
                <h2><i className="fas fa-palette"></i> Appearance</h2>
                <div className="PG-setting-item">
                  <div className="PG-setting-info">
                    <h3>Theme</h3>
                    <p>Choose your preferred color scheme</p>
                  </div>
                  <div className="PG-theme-options">
                    <button className={`PG-theme-btn-setting ${theme === 'dark' ? 'active' : ''}`} onClick={() => handleThemeToggle('dark')}>
                      <i className="fas fa-moon"></i> Dark
                    </button>
                    <button className={`PG-theme-btn-setting ${theme === 'light' ? 'active' : ''}`} onClick={() => handleThemeToggle('light')}>
                      <i className="fas fa-sun"></i> Light
                    </button>
                  </div>
                </div>
              </div>
              <div className="PG-settings-section">
                <h2><i className="fas fa-info-circle"></i> About</h2>
                <div className="PG-about-info-content">
                  <div className="PG-about-item">
                    <span className="PG-about-label">Application</span>
                    <span className="PG-about-value">G-PGS</span>
                  </div>
                  <div className="PG-about-item">
                    <span className="PG-about-label">Version</span>
                    <span className="PG-about-value">1.0.0</span>
                  </div>
                  <div className="PG-about-item">
                    <span className="PG-about-label">Account ID</span>
                    <span className="PG-about-value">{apiKey || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : activeTab === 'help' ? (
          <div className="PG-help-page">
            <div className="PG-help-container">
              <div className="PG-help-header">
                <h1>Documentation</h1>
                <p>Everything you need to get started with G-PGS</p>
              </div>

              {/* Quick Links */}
              <div className="PG-help-grid">
                <div className="PG-help-card">
                  <div className="PG-help-card-icon"><i className="fas fa-code"></i></div>
                  <div className="PG-help-card-content">
                    <h3>API Integration</h3>
                    <p>REST endpoints for programmatic access to G-PGS</p>
                    <button className="PG-help-link" onClick={() => setActiveTab('api')}>
                      <i className="fas fa-arrow-right"></i> View API Docs
                    </button>
                  </div>
                </div>
                <div className="PG-help-card">
                  <div className="PG-help-card-icon"><i className="fas fa-envelope"></i></div>
                  <div className="PG-help-card-content">
                    <h3>Contact Support</h3>
                    <p>Get help from our team for technical issues or feedback</p>
                    <a href="mailto:hello@phenomelongevity.com" className="PG-help-link">
                      <i className="fas fa-paper-plane"></i> hello@phenomelongevity.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Getting Started Guide */}
              <div className="PG-docs-section">
                <h2>Getting Started</h2>
                <p className="PG-docs-intro">G-PGS computes polygenic risk scores (PRS) from VCF genotype data. Upload a file, wait for processing, and generate a multilingual PDF report with population-calibrated risk percentiles.</p>

                {/* Step 1 */}
                <div className="PG-docs-step">
                  <div className="PG-docs-step-header">
                    <span className="PG-docs-step-num">1</span>
                    <h3>Create a New Job</h3>
                  </div>
                  <p>From the Home tab, fill in the sample name, gender, and date of birth on the right panel. Then drag and drop a <code>.vcf.gz</code> file into the upload zone and click <strong>Submit Job</strong>.</p>
                  <div className="PG-docs-screenshot">
                    <img src={docScreenshot1} alt="New PRS Job form" />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="PG-docs-step">
                  <div className="PG-docs-step-header">
                    <span className="PG-docs-step-num">2</span>
                    <h3>Monitor Job Progress</h3>
                  </div>
                  <p>Your job appears in the jobs list on the left. Status updates automatically every few seconds. A job goes through: <code>pending</code> → <code>queued</code> → <code>running</code> → <code>completed</code>.</p>
                  <div className="PG-docs-screenshot">
                    <img src={docScreenshot2} alt="Jobs list with running and completed jobs" />
                  </div>
                </div>

                {/* Step 3 */}
                <div className="PG-docs-step">
                  <div className="PG-docs-step-header">
                    <span className="PG-docs-step-num">3</span>
                    <h3>View Results & Select Language</h3>
                  </div>
                  <p>Click on a completed job to open the results panel and download the full result bundle.</p>
                  <div className="PG-docs-screenshot">
                    <img src={docScreenshot4} alt="Results modal with language selector" />
                  </div>
                </div>

                {/* Step 4 */}
                <div className="PG-docs-step">
                  <div className="PG-docs-step-header">
                    <span className="PG-docs-step-num">4</span>
                    <h3>Generate a Report</h3>
                  </div>
                  <p>Click Generate and the PDF renders inline once ready. The report includes clinical risk overviews, population-calibrated percentiles, and variant-level detail.</p>
                  <div className="PG-docs-screenshot">
                    <img src={docScreenshot3} alt="Report generation with PDF preview" />
                  </div>
                </div>
              </div>

              {/* Supported File Formats */}
              <div className="PG-docs-section">
                <h2>Supported Formats</h2>
                <div className="PG-docs-formats">
                  <div className="PG-docs-format-item">
                    <i className="fas fa-file-alt"></i>
                    <div>
                      <h4>.vcf.gz</h4>
                      <p>Compressed VCF file containing genotype data. This is the only required input.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Report Languages */}
              <div className="PG-docs-section">
                <h2>Report Languages</h2>
                <p>Reports can be generated in any of the following languages. Language is selected at report generation time, not during job submission.</p>
                <div className="PG-docs-lang-grid">
                  {['English', 'Turkish', 'Korean', 'Japanese', 'Chinese', 'Arabic', 'Spanish'].map(lang => (
                    <span key={lang} className="PG-docs-lang-tag">{lang}</span>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="PG-help-faq">
                <h2>Frequently Asked Questions</h2>
                <div className="PG-faq-list">
                  <div className="PG-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> What file formats are supported?</h4>
                    <p>G-PGS accepts <code>.vcf.gz</code> files containing genotype data for polygenic risk score computation.</p>
                  </div>
                  <div className="PG-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> How long does a PRS computation take?</h4>
                    <p>Processing time varies by file size. Most jobs complete within 15-45 minutes.</p>
                  </div>
                  <div className="PG-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> Is my data secure?</h4>
                    <p>Yes. All uploads are encrypted in transit and at rest. Data is processed in isolated environments and automatically deleted after analysis.</p>
                  </div>
                  <div className="PG-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> Can I generate reports in multiple languages?</h4>
                    <p>Yes. You can generate a new report for the same job in a different language at any time from the results panel.</p>
                  </div>
                  <div className="PG-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> What PRS methods are used?</h4>
                    <p>G-PGS integrates multiple PRS computation methods including PRS-CS, LDpred2, and SBayesR with population-calibrated percentile rankings.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : activeTab === 'api' ? (
          <CentralApiGuide
            serviceCode={SERVICE_CODE}
            serviceName="G-PGS"
            token={apiKey}
          />
        ) : <div className="PG-home">
            <header className="PG-home-header">
              <div className="PG-welcome-container">
                <div className="PG-welcome-logo-wrapper">
                  <img src={logoSrc} alt="PolyGen Logo" className="PG-welcome-logo" />
                </div>
                <div className="PG-welcome-text">
                  <h1>Welcome to G-PGS</h1>
                  <p>Polygenic risk score computation and analysis platform</p>
                </div>
              </div>
            </header>

            <div className="PG-home-content">
              {/* Left Column - Jobs */}
              <section className="PG-left-column">
                <div className="PG-section PG-jobs-section">
                  <h2><i className="fas fa-tasks"></i> PRS Jobs</h2>
                  <div className="PG-jobs-filter">
                    <div className="PG-search-input">
                      <i className="fas fa-search"></i>
                      <input type="text" placeholder="Search jobs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="running">Running</option>
                      <option value="completed">Completed</option>
                      <option value="queued">Queued</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div className="PG-jobs-list">
                    {isLoadingJobs ? (
                      <div className="PG-empty-state"><i className="fas fa-spinner fa-spin"></i><span>Loading...</span></div>
                    ) : filteredJobs.length === 0 ? (
                      <div className="PG-empty-state"><i className="fas fa-folder-open"></i><span>No jobs found</span></div>
                    ) : (
                      filteredJobs.map(job => (
                        <div key={job.job_id} className="PG-job-item" onClick={() => job.status === 'completed' ? openResultsModal(job) : null} style={{ cursor: job.status === 'completed' ? 'pointer' : 'default' }}>
                          <div className="PG-job-icon"><i className="fas fa-dna"></i></div>
                          <div className="PG-job-content">
                            <div className="PG-job-title">
                              {job.demo && <span className="PG-demo-badge">DEMO</span>}
                              {job.sample_name}
                            </div>
                            <div className="PG-job-subtitle">{job.gender} &middot; {job.age}</div>
                          </div>
                          <div className="PG-job-status-area">
                            <span className={`PG-job-type-badge ${job.status}`}>
                              <i className={`fas ${getStatusIcon(job.status)}`}></i> {job.status}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </section>

              {/* Right Column - New Job */}
              <aside className="PG-right-column">
                <div className="PG-section PG-newjob-section">
                  <h2><i className="fas fa-plus-circle"></i> New PRS Job</h2>

                  <div className="PG-form-group">
                    <label>Sample Name <span className="PG-required">*</span></label>
                    <input type="text" placeholder="e.g. John Doe" value={sampleName} onChange={(e) => setSampleName(e.target.value)} />
                  </div>

                  <div className="PG-form-row">
                    <div className="PG-form-group">
                      <label>Gender <span className="PG-required">*</span></label>
                      <select value={gender} onChange={(e) => setGender(e.target.value)}>
                        <option value="">Select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="PG-form-group">
                      <label>Date of Birth <span className="PG-required">*</span></label>
                      <input type="date" value={age} onChange={(e) => setAge(e.target.value)} />
                    </div>
                  </div>

                  {(pipeline?.sample_codes?.length || 0) > 1 && (
                    <div className="PG-form-group">
                      <label>Sample Type <span className="PG-required">*</span></label>
                      <select value={sampleCode} onChange={(e) => setSampleCode(e.target.value)}>
                        <option value="">Select...</option>
                        {pipeline.sample_codes.map((code) => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="PG-form-group">
                    <label>VCF File <span className="PG-required">*</span></label>
                    <div className={`PG-dropzone ${selectedFile ? 'has-file' : ''}`} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
                      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".vcf,.vcf.gz,.gz" style={{ display: 'none' }} />
                      {selectedFile ? (
                        <div className="PG-dropzone-file">
                          <i className="fas fa-file-alt"></i>
                          <div className="PG-dropzone-file-info">
                            <span className="PG-dropzone-filename">{selectedFile.name}</span>
                            <span className="PG-dropzone-filesize">{formatFileSize(selectedFile.size)}</span>
                          </div>
                          <button className="PG-dropzone-remove" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="PG-dropzone-empty">
                          <i className="fas fa-cloud-upload-alt"></i>
                          <span>Drop .vcf.gz file here or click to browse</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {isSubmitting && uploadProgress > 0 && (
                    <div className="PG-upload-progress">
                      <div className="PG-progress-bar">
                        <div className="PG-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                      </div>
                      <span className="PG-progress-text">{uploadProgress}%</span>
                    </div>
                  )}

                  <button className="PG-submit-btn" onClick={handleSubmit} disabled={isSubmitting || !sampleName || !gender || !age || !selectedFile || !pipeline || !sampleCode}>
                    {isSubmitting ? <><i className="fas fa-spinner fa-spin"></i> Submitting...</> : <><i className="fas fa-arrow-right"></i> Submit Job</>}
                  </button>
                </div>
                <WebserverDemoSection serviceCode={SERVICE_CODE} />
              </aside>
            </div>
          </div>}
      </main>

      <div className="PG-cookie-icon">
        <i className="fas fa-cookie"></i>
        <span className="PG-tooltip">{`ID: ${apiKey?.slice(0, 8)}...`}</span>
      </div>

      {/* Results Modal — FamilyRisk style */}
      {resultsJob && (
        <div className="PG-modal-overlay" onClick={closeResultsModal}>
          <div className="PG-results-modal" onClick={(e) => e.stopPropagation()}>

            {/* LEFT SIDEBAR */}
            <div className="PG-results-sidebar">
              {/* Job info */}
              <div className="PG-results-jobinfo">
                <div className="PG-results-jobinfo-header">
                  <i className="fas fa-dna"></i>
                  <span>{resultsJob.sample_name}</span>
                  <button className="PG-modal-close" onClick={closeResultsModal}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="PG-results-jobinfo-rows">
                  <div className="PG-results-jobinfo-row">
                    <span className="PG-results-jobinfo-label">Status</span>
                    <span className="PG-results-jobinfo-value">
                      <span className={`PG-job-type-badge ${resultsJob.status}`}>
                        <i className={`fas ${getStatusIcon(resultsJob.status)}`}></i> {resultsJob.status}
                      </span>
                    </span>
                  </div>
                  <div className="PG-results-jobinfo-row">
                    <span className="PG-results-jobinfo-label">Gender</span>
                    <span className="PG-results-jobinfo-value">{resultsJob.gender}</span>
                  </div>
                  <div className="PG-results-jobinfo-row">
                    <span className="PG-results-jobinfo-label">Age</span>
                    <span className="PG-results-jobinfo-value">{resultsJob.age}</span>
                  </div>
                  {resultsJob.created_at && (
                    <div className="PG-results-jobinfo-row">
                      <span className="PG-results-jobinfo-label">Created</span>
                      <span className="PG-results-jobinfo-value">{new Date(resultsJob.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="PG-results-jobinfo-row">
                    <span className="PG-results-jobinfo-label">Job ID</span>
                    <span className="PG-results-jobinfo-value PG-results-jobid">{resultsJob.job_id}</span>
                  </div>
                </div>
              </div>

              {/* Footer actions */}
              <div className="PG-results-sidebar-footer">
                <button className="PG-results-download-btn" onClick={handleDownloadResults} title="Download all results">
                  <i className="fas fa-download"></i> Download all results
                </button>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="PG-results-preview">
              <div className="PG-results-empty">
                <i className="fas fa-chart-bar"></i>
                <span>PRS results are ready</span>
                <p>Download the complete output bundle generated by the active G-PGS pipeline.</p>
              </div>
              <WebserverReportPanel
                serviceCode={SERVICE_CODE}
                token={apiKey}
                job={resultsJob}
                reportTypes={pipeline?.report_types || []}
              />
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default PolyGen;
